import React, { useState } from "react";
import { View, TextInput, Text, StyleSheet, type TextInputProps } from "react-native";
import { colors, radii, spacing } from "../../../styles/design-system";

export type AxioInputStatus = "default" | "error" | "disabled";

export interface AxioInputProps extends Omit<TextInputProps, "editable"> {
  label?: string;
  helperText?: string;
  errorText?: string;
  status?: AxioInputStatus;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const AxioInput: React.FC<AxioInputProps> = ({
  label,
  helperText,
  errorText,
  status = "default",
  leftIcon,
  rightIcon,
  onFocus,
  onBlur,
  ...textInputProps
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const isDisabled = status === "disabled";
  const hasError = status === "error" || !!errorText;

  const handleFocus: TextInputProps["onFocus"] = (event) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur: TextInputProps["onBlur"] = (event) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const borderColor = hasError ? colors.error[400] : isFocused ? colors.brand[500] : colors.grey[300];
  const borderWidth = hasError ? 1 : isFocused ? 3 : 1;
  const backgroundColor = isDisabled ? colors.grey[25] : colors.white;

  return (
    <View style={styles.container}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <View
        style={[
          styles.inputWrapper,
          {
            borderColor,
            borderWidth,
            backgroundColor,
            opacity: isDisabled ? 0.6 : 1,
          },
        ]}>
        {leftIcon ? <View style={styles.iconLeft}>{leftIcon}</View> : null}
        <TextInput
          {...textInputProps}
          style={[styles.input, textInputProps.style]}
          editable={!isDisabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholderTextColor={colors.grey[400]}
        />
        {rightIcon ? <View style={styles.iconRight}>{rightIcon}</View> : null}
      </View>
      {hasError && errorText ? (
        <Text style={styles.errorText}>{errorText}</Text>
      ) : helperText ? (
        <Text style={styles.helperText}>{helperText}</Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  label: {
    fontSize: 13,
    fontWeight: "500",
    color: colors.grey[700],
    marginBottom: spacing.xs,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: radii.md,
    paddingHorizontal: spacing.sm,
    height: 44,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: colors.grey[800],
    // Remove default browser border/outline on web
    borderWidth: 0,
    outlineStyle: "none",
  } as any,
  iconLeft: {
    marginRight: spacing.xs,
  },
  iconRight: {
    marginLeft: spacing.xs,
  },
  helperText: {
    marginTop: spacing.xs / 2,
    fontSize: 12,
    color: colors.grey[500],
  },
  errorText: {
    marginTop: spacing.xs / 2,
    fontSize: 12,
    color: colors.error[500],
  },
});

export default AxioInput;
